import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Truck, Users, Star, Building2, Store, School, Briefcase, ShieldCheck, Layers, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import ProductCard from "@/components/site/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import Seo from "@/components/site/Seo";

export default function Home() {
  const [homepage, setHomepage] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [reviews, setReviews] = useState([]);
  const s = useSettings();
  useEffect(() => {
    api.get("/homepage").then(r => setHomepage(r.data));
    api.get("/products", { params: { featured: true, limit: 8 } }).then(r => setFeatured(r.data));
    api.get("/products", { params: { new_arrival: true, limit: 8 } }).then(r => setNewArrivals(r.data));
    api.get("/categories").then(r => setCategories(r.data));
    api.get("/brands").then(r => setBrands(r.data));
    api.get("/reviews").then(r => setReviews(r.data));
  }, []);
  const hero = homepage?.hero || {};
  const whyItems = homepage?.why_us_items || [];
  const whoWeSupply = homepage?.who_we_supply || [];
  const howItWorks = homepage?.how_it_works || [];
  const bulkCta = homepage?.bulk_cta || {};

  return (
    <div>
      <Seo page="home"/>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url("https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=85")`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/95 to-[#0F172A]/60" />
        <div className="relative container-x py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs mb-6">
              <span className="w-2 h-2 rounded-full bg-[#EA580C]"/>
              Bengaluru's Trusted B2B IT Supplier
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {hero.headline || "Wholesale IT Products. Reliable Supply. Business Ready."}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl">
              {hero.subheadline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={hero.primary_cta_link || "/products"}>
                <Button size="lg" className="btn-orange" data-testid="hero-cta-primary">{hero.primary_cta_label || "Browse Products"} <ArrowRight className="w-4 h-4 ml-1"/></Button>
              </Link>
              <Link to={hero.secondary_cta_link || "/request-quote"}>
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-[#0F172A]" data-testid="hero-cta-secondary">{hero.secondary_cta_label || "Request a Quote"}</Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-300">
              <div className="flex items-center gap-2"><PackageCheck className="w-4 h-4 text-[#EA580C]"/> Flexible Quantities</div>
              <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#EA580C]"/> Wholesale Supply</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#EA580C]"/> Availability-Based Quotes</div>
            </div>
          </div>
          <div className="hidden lg:block">
            <img src={s.hero_poster || hero.image || "https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=800&q=85"}
                 alt="REXTECH IT Solutions - Wholesale IT Products" className="rounded-lg shadow-2xl border-4 border-white/10 bg-white"/>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section-py bg-[#F8FAFC]" data-testid="categories-section">
        <div className="container-x">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Catalogue</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">Shop by Category</h2>
            </div>
            <Link to="/products" className="hidden sm:inline-flex link-orange font-semibold text-sm items-center gap-1">View all <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map(c => (
              <Link key={c.id} to={`/products?category=${encodeURIComponent(c.name)}`}
                className="group bg-white border border-[#E2E8F0] rounded-md overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`category-card-${c.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-slate-50">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                </div>
                <div className="p-4">
                  <div className="font-heading font-bold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</div>
                  <div className="mt-3 text-xs font-semibold text-[#EA580C] flex items-center gap-1">View Products <ArrowRight className="w-3 h-3"/></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section-py" data-testid="featured-section">
        <div className="container-x">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Featured</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">Featured Products</h2>
            </div>
            <Link to="/products" className="hidden sm:inline-flex link-orange font-semibold text-sm items-center gap-1">See all <ArrowRight className="w-4 h-4"/></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map(p => <ProductCard key={p.id} product={p}/>)}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-py bg-[#0F172A] text-white" data-testid="why-us-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Why REXTECH</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">Built for B2B IT Buyers</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whyItems.map((w, i) => (
              <div key={w.title || w.heading || w} className="bg-white/5 border border-white/10 p-6 rounded-md">
                <div className="w-10 h-10 rounded-md bg-[#EA580C] flex items-center justify-center mb-3">
                  {[<Zap/>, <Layers/>, <PackageCheck/>, <Building2/>, <ShieldCheck/>, <Truck/>][i % 6]}
                </div>
                <div className="font-heading font-bold text-lg">{w.title}</div>
                <div className="text-sm text-slate-300 mt-1">{w.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-py" data-testid="how-it-works-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Process</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">How It Works</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {howItWorks.map((s) => (
              <div key={s.step || s.title} className="bg-white border border-[#E2E8F0] rounded-md p-5 text-center">
                <div className="w-10 h-10 mx-auto rounded-full bg-[#0F172A] text-white font-heading font-bold flex items-center justify-center">{s.step}</div>
                <div className="font-heading font-bold mt-3">{s.title}</div>
                <div className="text-xs text-slate-500 mt-1">{s.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SUPPLY */}
      <section className="section-py bg-[#F8FAFC]" data-testid="who-we-supply-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Customers</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">Who We Supply</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {whoWeSupply.map((w, i) => (
              <div key={w} className="bg-white border border-[#E2E8F0] p-4 rounded-md flex items-center gap-3">
                {[<Store className="w-5 h-5 text-[#EA580C]"/>, <Building2 className="w-5 h-5 text-[#EA580C]"/>, <Users className="w-5 h-5 text-[#EA580C]"/>, <Briefcase className="w-5 h-5 text-[#EA580C]"/>, <School className="w-5 h-5 text-[#EA580C]"/>][i % 5]}
                <div className="text-sm font-semibold">{w}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BULK CTA */}
      <section className="section-py" data-testid="bulk-cta-section">
        <div className="container-x">
          <div className="bg-[#0F172A] rounded-lg p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#EA580C]/20"/>
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <h3 className="font-heading font-extrabold text-white text-3xl">{bulkCta.headline}</h3>
                <p className="text-slate-300 mt-3">{bulkCta.text}</p>
              </div>
              <Link to={bulkCta.cta_link || "/request-quote"} className="justify-self-start md:justify-self-end">
                <Button size="lg" className="btn-orange" data-testid="bulk-cta-button">{bulkCta.cta_label || "Request Wholesale Quote"}</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      {newArrivals.length > 0 && (
        <section className="section-py bg-[#F8FAFC]" data-testid="new-arrivals-section">
          <div className="container-x">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Just In</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">New Arrivals</h2>
              </div>
              <Link to="/new-arrivals" className="link-orange font-semibold text-sm inline-flex items-center gap-1">View all <ArrowRight className="w-4 h-4"/></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {newArrivals.slice(0, 4).map(p => <ProductCard key={p.id} product={p}/>)}
            </div>
          </div>
        </section>
      )}

      {/* BRANDS */}
      <section className="section-py" data-testid="brands-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Brands</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">Brands We Supply</h2>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {brands.map(b => (
              <div key={b.id} className="bg-white border border-[#E2E8F0] rounded-md py-6 px-3 text-center font-heading font-bold text-slate-700 hover:border-[#EA580C] transition-colors flex items-center justify-center min-h-[72px]">
                {b.logo ? <img src={b.logo} alt={b.name} className="h-8 max-w-full object-contain"/> : b.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-py bg-[#F8FAFC]" data-testid="reviews-section">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="text-xs uppercase tracking-widest text-[#EA580C] font-semibold">Trust</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl mt-1">{s.google_rating || 5.0} ★ on Google</h2>
            <p className="text-slate-600 mt-2">Based on {s.google_reviews_count || 12} verified Google reviews.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {reviews.slice(0, 3).map(r => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-md p-6">
                <div className="flex gap-0.5 text-amber-400 mb-3">
                  {Array.from({length: r.rating || 5}).map((_, i) => <Star key={`${r.id}-star-${i}`} className="w-4 h-4 fill-amber-400"/>)}
                </div>
                <p className="text-slate-700 text-sm">"{r.review_text}"</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div className="font-semibold text-slate-900">{r.reviewer_name}</div>
                  <div className="text-slate-500">{r.source}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
